import React, { useEffect, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import '../../styles/user/submittedVerification.css';
import {
  fetchSubmittedCandidates
} from '../../services/user/api';
import { Search } from 'lucide-react';

const SubmittedVerificationPage = ({onViewReportTwo }) => {
  const [candidates, setCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
const startEntry = totalElements === 0 ? 0 : currentPage * entriesPerPage + 1;
const endEntry = Math.min(currentPage * entriesPerPage + candidates.length, totalElements);

  const loadSubmittedCandidates = async () => {
  try {
    setLoading(true);

const response = await fetchSubmittedCandidates(currentPage, entriesPerPage, search);

    if (response?.data?.status) {
      const data = response.data.data;
      setCandidates(data.content || []);
      setTotalElements(data.totalElements || 0);
    } else {
      setFetchError(response.data.message || 'Failed to fetch submitted candidates.');
    }
  } catch (error) {
    setFetchError('An error occurred while fetching data.');
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  loadSubmittedCandidates();
}, [currentPage, entriesPerPage, search]);



  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'status-tag verified';
      case 'completed':
        return 'status-tag completed';
      case 'work in progress':
        return 'status-tag work-in-progress';
      case 'login expired':
        return 'status-tag login-expired';
      case 'yet to start':
        return 'status-tag pending-submission';
      default:
        return 'status-tag';
    }
  };

 
  const filteredCandidates = candidates.filter((candidate) => {
    const searchText = search.toLowerCase();
    return (
      String(candidate.candidateid || '').toLowerCase().includes(searchText) ||
      String(candidate.fullname || '').toLowerCase().includes(searchText) ||
      String(candidate.email || '').toLowerCase().includes(searchText) ||
      String(candidate.mobileNumber || '').toLowerCase().includes(searchText) ||
      String(candidate.dateOfBirth || '').toLowerCase().includes(searchText) ||
      String(candidate.caseInitiatedDateOnlyAsString || '').toLowerCase().includes(searchText) ||
      String(candidate.caseInitiatedTimeOnly || '').toLowerCase().includes(searchText) ||
      String(candidate.bgcStatus || '').toLowerCase().includes(searchText)
    );
  });


  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    if (!sortField) return 0;

    let aValue = a[sortField] ?? '';
    let bValue = b[sortField] ?? '';

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (sortField.toLowerCase().includes('date')) {
      return sortOrder === 'asc' ? new Date(aValue) - new Date(bValue) : new Date(bValue) - new Date(aValue);
    }

    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const renderSortArrow = (field) => {
    if (sortField !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const totalPages = Math.ceil(totalElements / entriesPerPage);

  return (
    <>
      <p className="client-header">Submitted for Verification</p>

      <div className="client-wrapper">
        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{' '}
            entries
          </div>


          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                id="client-search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search by any field..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        {fetchError && <div className="error-message">{fetchError}</div>}

        <div className="clinet-table-container">
          {loading ? (
            <div className="loading">Loading Candidates...</div>
          ) : (
            <table className="candidates-clinet-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('candidateid')}>Candidate ID {renderSortArrow('candidateid')}</th>
                  <th onClick={() => handleSort('fullname')}>Name {renderSortArrow('fullname')}</th>
                  <th onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                  <th onClick={() => handleSort('mobileNumber')}>Mobile Number {renderSortArrow('mobileNumber')}</th>
                  <th onClick={() => handleSort('dateOfBirth')}>DOB {renderSortArrow('dateOfBirth')}</th>
                  <th onClick={() => handleSort('caseInitiatedDateOnlyAsString')}>Case Initiated Date {renderSortArrow('caseInitiatedDateOnlyAsString')}</th>
                  <th onClick={() => handleSort('caseInitiatedTimeOnly')}>Case Initiated Time {renderSortArrow('caseInitiatedTimeOnly')}</th>
                  <th onClick={() => handleSort('bgcStatus')}>Status {renderSortArrow('bgcStatus')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCandidates.length > 0 ? (
                  sortedCandidates.map((candidate) => (
                    <tr key={candidate.candidateid}>
                      <td>{candidate.candidateid}</td>
                      <td>{candidate.fullname}</td>
                      <td>{candidate.email}</td>
                      <td>{candidate.mobileNumber}</td>
                      <td>{candidate.dateOfBirth}</td>
                      <td>{candidate.caseInitiatedDateOnlyAsString || 'N/A'}</td>
                      <td>{candidate.caseInitiatedTimeOnly || 'N/A'}</td>
                      <td>
                        <span className={getStatusClass(candidate.bgcStatus)}>
                          {candidate.bgcStatus || 'N/A'}
                        </span>
                      </td>
                      <td className="client-action-btn">
                        <button
                          className="client-action-btn"
                          onClick={() => onViewReportTwo(candidate.candidateid)}
                          title="View Report"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-data">
                      No matching candidates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalElements > 0 && (
          <div className="pagination">
            <div className="pagination-summary">
              Showing {currentPage * entriesPerPage + 1} to {Math.min((currentPage + 1) * entriesPerPage, totalElements)} of {totalElements} entries
            </div>

            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                ←
              </button>

              <button
                className={`page-btn ${"active"}`}
                onClick={() => setCurrentPage(currentPage)}
              >
                {currentPage + 1}
              </button>

              {currentPage + 1 < totalPages && (
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 2}
                </button>
              )}

              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SubmittedVerificationPage;
